/**
 * Value Drivers Tab (DC-1367)
 *
 * Shows data from IntelligencePrism — Pistoia-validated ROI projections
 * with expandable driver/task accordion.
 * Extracted from StrategicIntelligenceDashboard.tsx.
 */

import React, { useState } from 'react';
import type { PrismData } from '../IntelligencePrism';

interface ValueDriversTabProps {
  prismData: PrismData | null;
  hasInsights: boolean;
  onNavigateToActionPlan?: () => void;
}

export const ValueDriversTab: React.FC<ValueDriversTabProps> = ({
  prismData,
  hasInsights,
  onNavigateToActionPlan
}) => {
  const [expandedDrivers, setExpandedDrivers] = useState<Set<string>>(new Set());

  const valueDrivers = prismData?.valueDrivers || [];
  const allTasks = prismData?.tasks || [];
  /*
   * The COMPLETE total, or null (S4d follow-up). `totals.roi` was a priced
   * subtotal, and `|| 0` additionally turned an unavailable total into a
   * displayed zero.
   */
  const totalValue = prismData?.totals?.valueComplete ?? null;
  const pricedSubtotal = prismData?.totals?.valuePricedSubtotal ?? null;
  const unpricedTasks = prismData?.totals?.unpricedTasks ?? 0;
  const totalTasks = prismData?.totals?.tasks || 0;

  const toggleDriver = (driverId: string) => {
    setExpandedDrivers(prev => {
      const next = new Set(prev);
      if (next.has(driverId)) {
        next.delete(driverId);
      } else {
        next.add(driverId);
      }
      return next;
    });
  };

  const getTasksForDriver = (driverName: string) => {
    return allTasks.filter(task => task.pistoia_driver === driverName);
  };

  if (!hasInsights || !prismData) {
    return (
      <div className="value-drivers-empty">
        <div className="empty-icon">💰</div>
        <h3>Value Drivers</h3>
        <p>Generate AI insights to see Pistoia-validated ROI projections.</p>
      </div>
    );
  }

  if (valueDrivers.length === 0) {
    return (
      <div className="value-drivers-content">
        <p className="no-data">No value driver tasks available yet.</p>
      </div>
    );
  }

  return (
    <div className="value-drivers-content value-drivers-hierarchical">
      <div className="value-drivers-summary">
        {/*
          "Total Projected Value" over a priced subtotal, with `|| 0` turning an
          unavailable total into a displayed $0K (S4d follow-up). Three states now:
          complete, priced subtotal, or nothing priced at all.
        */}
        <div className="summary-stat">
          <span className="stat-value">
            {totalValue !== null
              ? `$${(totalValue / 1000).toFixed(0)}K`
              : pricedSubtotal !== null
                ? `$${(pricedSubtotal / 1000).toFixed(0)}K`
                : 'Unavailable'}
          </span>
          <span className="stat-label">
            {totalValue !== null ? 'Total value' : 'Priced subtotal — total unavailable'}
          </span>
          {unpricedTasks > 0 && (
            <span className="stat-note">
              {unpricedTasks} task{unpricedTasks === 1 ? '' : 's'} unpriced
            </span>
          )}
        </div>
        <div className="summary-stat">
          <span className="stat-value">{totalTasks}</span>
          <span className="stat-label">Tasks Identified</span>
        </div>
        <div className="summary-stat">
          <span className="stat-value">{valueDrivers.length}</span>
          <span className="stat-label">Value Drivers</span>
        </div>
      </div>

      <div className="driver-accordion">
        {valueDrivers.map((driver) => {
          const isExpanded = expandedDrivers.has(driver.id);
          const driverTasks = getTasksForDriver(driver.name);

          return (
            <div key={driver.id} className={`driver-group ${isExpanded ? 'expanded' : ''}`}>
              <div
                className="driver-header clickable"
                onClick={() => toggleDriver(driver.id)}
                title="Click to expand/collapse tasks"
              >
                <div className="driver-expand-icon">
                  {isExpanded ? '▼' : '▶'}
                </div>
                <div className="driver-info">
                  <span className="driver-name">{driver.name}</span>
                  <span className="driver-task-count">{driver.taskCount} tasks</span>
                </div>
                <div className="driver-value">
                  ${(driver.value / 1000).toFixed(0)}K
                </div>
              </div>

              {isExpanded && driverTasks.length > 0 && (
                <div className="driver-tasks">
                  {driverTasks.map((task) => (
                    <div key={task.id} className={`driver-task-item source-${task.source}`}>
                      <div className="task-header">
                        <span className={`task-priority priority-${task.priority}`}>
                          {task.priority}
                        </span>
                        <span className={`task-source source-${task.source}`}>
                          {task.source === 'org' ? 'ORG' : 'DATA'}
                        </span>
                        <span className="task-title">{task.title}</span>
                      </div>
                      {task.description && (
                        <div className="task-description">{task.description}</div>
                      )}
                      {task.implementation_steps && task.implementation_steps.length > 0 && (
                        <div className="task-steps">
                          {task.implementation_steps.map((step, idx) => {
                            let stepText = '';
                            if (typeof step === 'string') {
                              stepText = step;
                            } else if (step && typeof step === 'object') {
                              const stepObj = step as Record<string, unknown>;
                              const stepValue = stepObj.step ?? stepObj.action ?? stepObj.text;
                              stepText = typeof stepValue === 'string' ? stepValue : JSON.stringify(step);
                            }
                            return (
                              <div key={idx} className="task-step">
                                <span className="step-number">{idx + 1}.</span>
                                <span className="step-text">{stepText}</span>
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {task.value_usd && task.value_usd > 0 && (
                        <div className="task-value">
                          Value: ${(task.value_usd / 1000).toFixed(0)}K
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {isExpanded && driverTasks.length === 0 && (
                <div className="driver-tasks empty">
                  <p>No detailed tasks available for this driver.</p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="value-drivers-cta">
        <p className="cta-text">
          Ready to turn these insights into action? The Action Plan will organize these tasks
          into Quick Wins, Foundational work, and Strategic Bets with detailed project metrics.
        </p>
        <button
          className="btn-action-plan"
          onClick={() => onNavigateToActionPlan?.()}
        >
          📋 View Full Action Plan
        </button>
      </div>
    </div>
  );
};
